import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
  Navigate,
} from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import FixedBonusBanner from './components/layout/FixedBonusBanner';

// Pages
import HomePage from './pages/HomePage';
import Casino from './pages/casino';
import Slots from './pages/slots';
import Bet from './pages/bet';
import Login from './pages/login';
import Games from './pages/games';
import Application from './pages/Application';
import Bonuses from './pages/bonuses';
import Aviator from './pages/aviator';
import Crazytime from './pages/crazytime';
import NotFound from './pages/404';

/* ========== Animations ========== */
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  in:      { opacity: 1, y: 0 },
  out:     { opacity: 0, y: -20 },
};
const pageTransition = { type: 'tween', ease: 'anticipate', duration: 0.5 };

/* ========== i18n helpers (URL-only) ========== */
const getLangFromPath = (pathname) => (pathname.startsWith('/bn') ? 'bn' : 'en');
const getLangPath = (path, lang) => {
  if (lang === 'bn') {
    if (path.startsWith('/bn')) return path;
    return `/bn${path.startsWith('/') ? path : `/${path}`}`;
  }
  if (path.startsWith('/bn')) return path.replace(/^\/bn/, '') || '/';
  return path.startsWith('/') ? path : `/${path}`;
};

const isBrowser = () => typeof window !== 'undefined';

/* ---------- GEO helper ---------- */
async function detectGeoCountry() {
  try {
    const res = await fetch('https://ipapi.co/json/');
    if (!res.ok) throw new Error('geo failed');
    const data = await res.json();
    return (data?.country || '').toUpperCase(); // например "BD"
  } catch {
    return null;
  }
}

/* ========== ScrollToTop ========== */
function ScrollToTop() {
  const location = useLocation();
  React.useEffect(() => {
    if (location.hash) return;
    if (!isBrowser()) return;
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });
  }, [location.pathname, location.search]);
  return null;
}

function NotFoundRedirect() {
  const location = useLocation();
  const to = location.pathname.startsWith('/bn') ? '/bn/404' : '/404';
  return <Navigate to={to} replace />;
}

/* ========== Motion wrapper ========== */
function MotionPage({ children, routeKey }) {
  return (
    <motion.div
      key={routeKey}
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
    >
      {children}
    </motion.div>
  );
}

/* ========== Routes (lang via URL only) ========== */
function AnimatedRoutes({ lang }) {
  const location = useLocation();
  const wrap = (Comp) => (
    <MotionPage routeKey={`${location.pathname}-${lang}`}>
      <Comp lang={lang} />
    </MotionPage>
  );

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={`${location.pathname}-${lang}`}>
        {/* EN */}
        <Route path="/" element={wrap(HomePage)} />
        <Route path="/casino" element={wrap(Casino)} />
        <Route path="/slots" element={wrap(Slots)} />
        <Route path="/bet" element={wrap(Bet)} />
        <Route path="/login" element={wrap(Login)} />
        <Route path="/app" element={wrap(Application)} />
        <Route path="/games" element={wrap(Games)} />
        <Route path="/bonuses" element={wrap(Bonuses)} />
        <Route path="/aviator" element={wrap(Aviator)} />
        <Route path="/crazy-time" element={wrap(Crazytime)} />

        {/* BN */}
        <Route path="/bn" element={wrap(HomePage)} />
        <Route path="/bn/casino" element={wrap(Casino)} />
        <Route path="/bn/slots" element={wrap(Slots)} />
        <Route path="/bn/bet" element={wrap(Bet)} />
        <Route path="/bn/login" element={wrap(Login)} />
        <Route path="/bn/app" element={wrap(Application)} />
        <Route path="/bn/games" element={wrap(Games)} />
        <Route path="/bn/bonuses" element={wrap(Bonuses)} />
        <Route path="/bn/aviator" element={wrap(Aviator)} />
        <Route path="/bn/crazy-time" element={wrap(Crazytime)} />

        {/* 404 */}
        <Route path="/404" element={<NotFound />} />
        <Route path="/bn/404" element={<NotFound />} />

        <Route path="*" element={<NotFoundRedirect />} />
      </Routes>
    </AnimatePresence>
  );
}

/* ========== Chrome ========== */
function Chrome() {
  const location = useLocation();
  const navigate = useNavigate();
  const lang = getLangFromPath(location.pathname);
  const isNotFound = location.pathname === '/404' || location.pathname === '/bn/404';

  /* ---------- GEO redirect on first load ---------- */
  React.useEffect(() => {
    // Только при первом рендере на / (без префикса)
    if (!isBrowser()) return;
    if (location.pathname.startsWith('/bn')) return; // уже bn
    if (sessionStorage.getItem('geoChecked')) return;

    (async () => {
      const country = await detectGeoCountry();
      sessionStorage.setItem('geoChecked', '1');
      if (country === 'BD') {
        // Перенаправляем на BN-версию
        const target = getLangPath(location.pathname, 'bn') + (location.search || '') + (location.hash || '');
        navigate(target, { replace: true });
      }
      // Все остальные страны остаются на EN
    })();
  }, [location.pathname, location.search, location.hash, navigate]);

  const toggleLang = React.useCallback(() => {
    const next = lang === 'en' ? 'bn' : 'en';
    const target = getLangPath(location.pathname, next) + (location.search || '') + (location.hash || '');
    if (target !== location.pathname + (location.search || '') + (location.hash || '')) {
      navigate(target, { replace: true });
    }
  }, [lang, location.pathname, location.search, location.hash, navigate]);

  return (
    <div className="App">
      <ScrollToTop />
      {!isNotFound && <Header key={`hdr-${lang}`} lang={lang} onToggleLang={toggleLang} getLangPath={getLangPath} />}
      <main className="main-content">
        <AnimatedRoutes lang={lang} />
      </main>
      {!isNotFound && <FixedBonusBanner lang={lang} />}
      {!isNotFound && <Footer key={`ftr-${lang}`} lang={lang} getLangPath={getLangPath} />}
    </div>
  );
}

/* ========== Root ========== */
export default function App() {
  return (
    <Router>
      <Chrome />
    </Router>
  );
}
